import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminService } from '../../../../services';
import { useAuth } from '../../../../contexts/AuthContext';
import { canManageUser } from '../../../common/RoleGuard';
import type { User } from '../../../../types/auth';
import './AdminUserDetail.css';

// ── Helpers ──────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const ROLE_CONFIG: Record<string, { label: string; className: string }> = {
  user: { label: 'Utente', className: 'aud-badge--user' },
  admin: { label: 'Admin', className: 'aud-badge--admin' },
  superadmin: { label: 'SuperAdmin', className: 'aud-badge--superadmin' },
};

const STATUS_CONFIG: Record<
  string,
  { label: string; className: string; icon: string }
> = {
  full: {
    label: 'Completo',
    className: 'aud-badge--full',
    icon: 'bi-check-circle-fill',
  },
  free_only: {
    label: 'Free',
    className: 'aud-badge--free',
    icon: 'bi-person-fill',
  },
  suspended: {
    label: 'Bloccato',
    className: 'aud-badge--blocked',
    icon: 'bi-slash-circle-fill',
  },
};

const PROVIDER_LABEL: Record<string, string> = {
  email: 'Email e password',
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

// ── Info row ─────────────────────────────────────────────────

const InfoRow: React.FC<{
  icon: string;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="aud-info-row">
    <span className="aud-info-icon">
      <i className={`bi ${icon}`} aria-hidden="true" />
    </span>
    <span className="aud-info-label">{label}</span>
    <span className="aud-info-value">{value}</span>
  </div>
);

// ── Skeleton ─────────────────────────────────────────────────

const DetailSkeleton: React.FC = () => (
  <div className="aud-skeleton-wrap">
    <div className="aud-skeleton-hero">
      <div className="aud-skeleton aud-skeleton--avatar-lg" />
      <div className="aud-skeleton-hero-text">
        <div className="aud-skeleton aud-skeleton--name" />
        <div className="aud-skeleton aud-skeleton--email" />
        <div className="aud-skeleton aud-skeleton--badges" />
      </div>
    </div>
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="aud-skeleton aud-skeleton--row" />
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user: currentUser } = useAuth();
  const basePath = pathname.startsWith('/superadmin')
    ? '/superadmin'
    : '/admin';

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Azioni
  const [blockingId, setBlockingId] = useState(false);
  const [deletingId, setDeletingId] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [resetPwdSent, setResetPwdSent] = useState(false);
  const [previousStatus, setPreviousStatus] = useState<
    User['accountStatus'] | null
  >(null);

  useEffect(() => {
    if (!id) return;
    adminService
      .getUser(id)
      .then(setUser)
      .catch(() => setError('Utente non trovato.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleBlock = async () => {
    if (!user) return;
    const isBlocked = user.accountStatus === 'suspended';
    setBlockingId(true);
    try {
      await adminService.blockUser(user.id, !isBlocked);
      if (isBlocked) {
        // sblocco → ripristina stato precedente
        setUser((u) =>
          u ? { ...u, accountStatus: previousStatus ?? 'full' } : u
        );
        setPreviousStatus(null);
      } else {
        // blocco → salva stato corrente
        setPreviousStatus(user.accountStatus);
        setUser((u) => (u ? { ...u, accountStatus: 'suspended' } : u));
      }
    } catch {
      // TODO: toast errore
    } finally {
      setBlockingId(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    setDeletingId(true);
    try {
      await adminService.deleteUser(user.id);
      navigate(`${basePath}/utenti`, { replace: true });
    } catch {
      // TODO: toast errore
      setDeletingId(false);
      setConfirmDelete(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    setResettingPwd(true);
    try {
      await adminService.resetUserPassword(user.id);
      setResetPwdSent(true);
      setTimeout(() => setResetPwdSent(false), 4000);
    } catch {
      // TODO: toast errore
    } finally {
      setResettingPwd(false);
    }
  };

  const initials = (u: User) =>
    `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase();

  // Mai gestibile se è il proprio account o un ruolo pari/superiore
  // (rispecchia assertCanManage sul backend — qui solo per la UI).
  const canManage =
    user && currentUser ? canManageUser(currentUser, user) : false;

  // ── Render ────────────────────────────────────────────────

  if (error) {
    return (
      <div className="aud-page">
        <div className="aud-error" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
          <button
            className="aud-action-btn ms-3"
            onClick={() => navigate(`${basePath}/utenti`)}
          >
            Torna agli utenti
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aud-page">
      {/* ── Back ── */}
      <button
        className="aud-back-btn"
        onClick={() => navigate(`${basePath}/utenti`)}
      >
        <i className="bi bi-arrow-left me-2" />
        Utenti
      </button>

      {loading ? (
        <DetailSkeleton />
      ) : (
        user && (
          <>
            {/* ── Hero ── */}
            <div className="aud-hero">
              <div className="aud-hero-left">
                <div className="aud-avatar-lg">
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user.firstName} />
                  ) : (
                    initials(user)
                  )}
                </div>
                <div className="aud-hero-info">
                  <h1 className="aud-hero-name">
                    {user.firstName} {user.lastName}
                  </h1>
                  <p className="aud-hero-email">{user.email}</p>
                  <div className="aud-hero-badges">
                    <span
                      className={`aud-badge ${ROLE_CONFIG[user.role]?.className}`}
                    >
                      <i className="bi bi-shield-check me-1" />
                      {ROLE_CONFIG[user.role]?.label ?? user.role}
                    </span>
                    <span
                      className={`aud-badge ${STATUS_CONFIG[user.accountStatus]?.className}`}
                    >
                      <i
                        className={`bi ${STATUS_CONFIG[user.accountStatus]?.icon} me-1`}
                      />
                      {STATUS_CONFIG[user.accountStatus]?.label ??
                        user.accountStatus}
                    </span>
                    {!user.emailConfirmed && (
                      <span className="aud-badge aud-badge--unconfirmed">
                        <i className="bi bi-envelope-exclamation me-1" />
                        Email non confermata
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Azioni hero */}
              {canManage ? (
                <div className="aud-hero-actions">
                  <button
                    className={`aud-action-btn ${user.accountStatus === 'suspended' ? 'aud-action-btn--success' : 'aud-action-btn--warn'}`}
                    onClick={handleBlock}
                    disabled={blockingId}
                  >
                    {blockingId ? (
                      <>
                        <span className="aud-spinner me-2" />
                        Attendi…
                      </>
                    ) : user.accountStatus === 'suspended' ? (
                      <>
                        <i className="bi bi-unlock me-2" />
                        Sblocca utente
                      </>
                    ) : (
                      <>
                        <i className="bi bi-lock me-2" />
                        Blocca utente
                      </>
                    )}
                  </button>
                  <button
                    className="aud-action-btn aud-action-btn--danger"
                    onClick={() => setConfirmDelete(true)}
                    disabled={deletingId}
                  >
                    <i className="bi bi-trash me-2" />
                    Elimina utente
                  </button>
                </div>
              ) : (
                currentUser?.id === user.id && (
                  <p className="aud-hero-self-note">
                    <i className="bi bi-info-circle me-1" />
                    Questo è il tuo account: non puoi bloccarlo o eliminarlo da
                    qui.
                  </p>
                )
              )}
            </div>

            {/* ── Sezioni info ── */}
            <div className="aud-sections">
              {/* Dati account */}
              <section className="aud-section">
                <h2 className="aud-section-title">
                  <i className="bi bi-person-lines-fill me-2" />
                  Dati account
                </h2>
                <div className="aud-info-list">
                  <InfoRow
                    icon="bi-person"
                    label="Nome"
                    value={`${user.firstName} ${user.lastName}`}
                  />
                  <InfoRow
                    icon="bi-envelope"
                    label="Email"
                    value={user.email}
                  />
                  <InfoRow
                    icon="bi-calendar3"
                    label="Iscritto il"
                    value={formatDate(user.createdAt)}
                  />
                  <InfoRow
                    icon="bi-key"
                    label="Provider auth"
                    value={
                      PROVIDER_LABEL[user.authProvider] ?? user.authProvider
                    }
                  />
                  <InfoRow
                    icon="bi-patch-check"
                    label="Email confermata"
                    value={
                      <span
                        className={`aud-badge ${user.emailConfirmed ? 'aud-badge--full' : 'aud-badge--unconfirmed'}`}
                      >
                        {user.emailConfirmed ? 'Sì' : 'No'}
                      </span>
                    }
                  />
                  <InfoRow
                    icon="bi-fingerprint"
                    label="ID utente"
                    value={<code className="aud-code">{user.id}</code>}
                  />
                  {canManage && user.authProvider === 'email' && (
                    <InfoRow
                      icon="bi-key"
                      label="Password"
                      value={
                        <button
                          className="aud-action-btn"
                          onClick={handleResetPassword}
                          disabled={resettingPwd}
                        >
                          {resettingPwd ? (
                            <>
                              <span className="aud-spinner me-2" />
                              Invio…
                            </>
                          ) : resetPwdSent ? (
                            <>
                              <i className="bi bi-check2 me-2" />
                              Email inviata
                            </>
                          ) : (
                            <>
                              <i className="bi bi-envelope me-2" />
                              Reimposta password
                            </>
                          )}
                        </button>
                      }
                    />
                  )}
                </div>
              </section>

              {/* Accessi e corsi */}
              <section className="aud-section">
                <h2 className="aud-section-title">
                  <i className="bi bi-collection-play me-2" />
                  Accessi e corsi
                </h2>
                <div className="aud-info-list">
                  <InfoRow
                    icon="bi-heart-pulse"
                    label="Fit Anatomy"
                    value={
                      <span
                        className={`aud-badge ${user.hasFitAnatomyAccess ? 'aud-badge--full' : 'aud-badge--free'}`}
                      >
                        {user.hasFitAnatomyAccess
                          ? 'Accesso attivo'
                          : 'Nessun accesso'}
                      </span>
                    }
                  />
                  <InfoRow
                    icon="bi-play-circle"
                    label="Corsi acquistati"
                    value={
                      user.purchasedCourseIds &&
                      user.purchasedCourseIds.length > 0 ? (
                        <div className="aud-course-list">
                          {user.purchasedCourseIds.map((id) => (
                            <span
                              key={id}
                              className="aud-badge aud-badge--course"
                            >
                              {id}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="aud-muted">
                          Nessun corso acquistato
                        </span>
                      )
                    }
                  />
                </div>
              </section>

              {/* Zona pericolosa */}
              {canManage && (
                <section className="aud-section aud-section--danger">
                  <h2 className="aud-section-title aud-section-title--danger">
                    <i className="bi bi-exclamation-triangle me-2" />
                    Zona pericolosa
                  </h2>
                  <p className="aud-danger-note">
                    Le seguenti azioni sono irreversibili. Procedi con cautela.
                  </p>
                  <div className="aud-danger-actions">
                    <div className="aud-danger-row">
                      <div>
                        <p className="aud-danger-row__title">
                          {user.accountStatus === 'suspended'
                            ? 'Sblocca account'
                            : 'Blocca account'}
                        </p>
                        <p className="aud-danger-row__desc">
                          {user.accountStatus === 'suspended'
                            ? "Ripristina l'accesso alla piattaforma."
                            : "Impedisce all'utente di accedere alla piattaforma."}
                        </p>
                      </div>
                      <button
                        className={`aud-action-btn ${user.accountStatus === 'suspended' ? 'aud-action-btn--success' : 'aud-action-btn--warn'}`}
                        onClick={handleBlock}
                        disabled={blockingId}
                      >
                        {user.accountStatus === 'suspended' ? (
                          <>
                            <i className="bi bi-unlock me-2" />
                            Sblocca
                          </>
                        ) : (
                          <>
                            <i className="bi bi-lock me-2" />
                            Blocca
                          </>
                        )}
                      </button>
                    </div>

                    <div className="aud-danger-row">
                      <div>
                        <p className="aud-danger-row__title">Elimina account</p>
                        <p className="aud-danger-row__desc">
                          Rimuove l'utente e tutti i suoi dati in modo
                          permanente.
                        </p>
                      </div>
                      <button
                        className="aud-action-btn aud-action-btn--danger"
                        onClick={() => setConfirmDelete(true)}
                        disabled={deletingId}
                      >
                        <i className="bi bi-trash me-2" />
                        Elimina
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </>
        )
      )}

      {/* ── Confirm delete dialog ── */}
      {confirmDelete && user && (
        <div className="aud-overlay" role="dialog" aria-modal="true">
          <div className="aud-dialog">
            <div className="aud-dialog__icon">
              <i className="bi bi-exclamation-triangle-fill" />
            </div>
            <h2 className="aud-dialog__title">Eliminare questo utente?</h2>
            <p className="aud-dialog__body">
              Stai per eliminare{' '}
              <strong>
                {user.firstName} {user.lastName}
              </strong>{' '}
              ({user.email}). L'operazione è irreversibile e rimuoverà tutti i
              dati, progressi e accessi ai corsi.
            </p>
            <div className="aud-dialog__actions">
              <button
                className="aud-action-btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deletingId}
              >
                Annulla
              </button>
              <button
                className="aud-action-btn aud-action-btn--danger"
                onClick={handleDelete}
                disabled={deletingId}
              >
                {deletingId ? (
                  <>
                    <span className="aud-spinner me-2" />
                    Eliminazione…
                  </>
                ) : (
                  'Elimina definitivamente'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;
